import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Autocomplete,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as BackIcon,
  People as GroupIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  subscribeToMessages,
  sendMessage,
  markAsRead,
  getConversationDisplayName,
  addParticipantsToConversation,
  updateConversationName
} from '../../services/messagingService';
import MessageBubble from './MessageBubble';
import logger from '../../utils/logger';

/**
 * Conversation view — shows message thread + input.
 * For group/family chats, includes a side panel to view/manage members.
 */
const ConversationView = ({ conversation, memberMap, onBack }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Group management state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [memberError, setMemberError] = useState('');

  const familyCode = profile?.familyCode;
  const isGroupOrFamily = conversation?.type === 'group' || conversation?.type === 'family';
  const isGroup = conversation?.type === 'group';

  const displayName = conversation
    ? getConversationDisplayName(conversation, currentUser?.uid, memberMap)
    : '';

  // Subscribe to messages
  useEffect(() => {
    if (!familyCode || !conversation?.id) return;

    setLoading(true);
    const unsubscribe = subscribeToMessages(familyCode, conversation.id, (msgs) => {
      setMessages(msgs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [familyCode, conversation?.id]);

  // Mark as read when conversation opens or new messages arrive
  useEffect(() => {
    if (!familyCode || !conversation?.id || !currentUser?.uid) return;

    markAsRead(familyCode, conversation.id, currentUser.uid).catch((err) => {
      logger.warn('Error marking messages as read:', err);
    });
  }, [familyCode, conversation?.id, currentUser?.uid, messages.length]);

  // Auto-scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when conversation changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [conversation?.id]);

  // Close drawer when conversation changes
  useEffect(() => {
    setDrawerOpen(false);
    setEditingName(false);
    setAddingMembers(false);
  }, [conversation?.id]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending || !familyCode || !conversation?.id) return;

    setSending(true);
    setNewMessage('');

    try {
      await sendMessage(familyCode, conversation.id, {
        content: text,
        authorId: currentUser.uid,
        authorName: profile?.displayName
          || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
          || currentUser.displayName
          || 'User',
        authorPhoto: currentUser.photoURL || profile?.photoURL || null
      });
    } catch (err) {
      logger.error('Error sending message:', err);
      setNewMessage(text); // Restore the message so user can retry
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !familyCode || !conversation?.id) return;

    if (!file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) return; // 10MB limit

    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `families/${familyCode}/conversations/${conversation.id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);

      await sendMessage(familyCode, conversation.id, {
        content: '',
        authorId: currentUser.uid,
        authorName: profile?.displayName
          || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
          || currentUser.displayName
          || 'User',
        authorPhoto: currentUser.photoURL || profile?.photoURL || null,
        type: 'image',
        imageUrl
      });
    } catch (err) {
      logger.error('Error sending photo:', err);
    } finally {
      setUploadingPhoto(false);
      if (e.target) e.target.value = '';
    }
  };

  // ─── Group Management Handlers ─────────────────────────────────

  const handleOpenDrawer = () => {
    setDrawerOpen(true);
    setEditingName(false);
    setAddingMembers(false);
    setMemberError('');
  };

  const handleStartEditName = () => {
    setEditName(conversation?.name || displayName);
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editName.trim() || !familyCode || !conversation?.id) return;
    setSavingName(true);
    try {
      await updateConversationName(familyCode, conversation.id, editName.trim());
      setEditingName(false);
    } catch (err) {
      logger.error('Error updating group name:', err);
      setMemberError('Failed to update group name');
    } finally {
      setSavingName(false);
    }
  };

  const handleStartAddMembers = async () => {
    setAddingMembers(true);
    setSelectedNewMembers([]);
    setMemberError('');

    // Load all family members and filter out current participants
    try {
      const membersSnapshot = await getDocs(collection(db, 'families', familyCode, 'members'));
      const currentParticipants = new Set(conversation?.participants || []);
      const available = [];

      membersSnapshot.forEach((doc) => {
        if (!currentParticipants.has(doc.id)) {
          const data = doc.data();
          available.push({
            id: doc.id,
            name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
            photoURL: data.photoURL || null,
            email: data.email
          });
        }
      });

      setAvailableMembers(available);
    } catch (err) {
      logger.error('Error loading available members:', err);
      setMemberError('Failed to load family members');
    }
  };

  const handleSaveNewMembers = async () => {
    if (selectedNewMembers.length === 0) return;
    setSavingMembers(true);
    setMemberError('');

    try {
      await addParticipantsToConversation(
        familyCode,
        conversation.id,
        selectedNewMembers.map(m => m.id)
      );
      setAddingMembers(false);
      setSelectedNewMembers([]);
    } catch (err) {
      logger.error('Error adding members:', err);
      setMemberError('Failed to add members. Please try again.');
    } finally {
      setSavingMembers(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────

  if (!conversation) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary'
        }}
      >
        <Typography variant="h6">Select a conversation to start messaging</Typography>
      </Box>
    );
  }

  // Determine if we should show author name/avatar on each message
  const shouldShowAuthors = isGroupOrFamily;

  // Group consecutive messages from same author (don't repeat name/avatar)
  const shouldShowAuthorForMessage = (msg, index) => {
    if (!shouldShowAuthors) return false;
    if (msg.authorId === currentUser?.uid) return false;
    if (index === 0) return true;
    return messages[index - 1]?.authorId !== msg.authorId;
  };

  // Current participants with their details
  const participantList = (conversation.participants || []).map(id => {
    const member = memberMap[id];
    return {
      id,
      name: member?.displayName || member?.name || 'Unknown',
      photoURL: member?.photoURL || null,
      isCurrentUser: id === currentUser?.uid
    };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        {/* Back button (mobile) */}
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ display: { md: 'none' } }}>
            <BackIcon />
          </IconButton>
        )}

        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
          {conversation.type === 'direct' ? <PersonIcon /> : <GroupIcon />}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {conversation.type === 'family'
              ? `${conversation.participants?.length || 0} members`
              : conversation.type === 'group'
                ? `${conversation.participants?.length || 0} members`
                : 'Direct Message'}
          </Typography>
        </Box>

        {/* Group info button */}
        {isGroupOrFamily && (
          <IconButton onClick={handleOpenDrawer} size="small" title="Group info">
            <InfoIcon />
          </IconButton>
        )}

        {conversation.type !== 'direct' && (
          <Chip
            label={conversation.type === 'family' ? 'Family' : 'Group'}
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography variant="body1">No messages yet</Typography>
            <Typography variant="body2">Send the first message to start the conversation!</Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg, index) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwn={msg.authorId === currentUser?.uid}
                showAuthor={shouldShowAuthorForMessage(msg, index)}
              />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Message input */}
      <Box
        component="form"
        onSubmit={handleSend}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          bgcolor: 'background.paper'
        }}
      >
        {/* Photo attachment button */}
        <input
          type="file"
          ref={photoInputRef}
          onChange={handlePhotoSelect}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <IconButton
          onClick={() => photoInputRef.current?.click()}
          disabled={uploadingPhoto || sending}
          color="primary"
          size="small"
          title="Send a photo"
        >
          {uploadingPhoto ? <CircularProgress size={20} /> : <PhotoIcon />}
        </IconButton>

        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={4}
          disabled={sending}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3
            }
          }}
        />
        <IconButton
          type="submit"
          color="primary"
          disabled={!newMessage.trim() || sending}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
          }}
        >
          {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>

      {/* ─── Group Info Drawer ──────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 340 } } }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drawer header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ mr: 1 }}>
              <CloseIcon />
            </IconButton>
            <Typography variant="h6">
              {conversation.type === 'family' ? 'Family Chat Info' : 'Group Info'}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {memberError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMemberError('')}>
              {memberError}
            </Alert>
          )}

          {/* Group Name (editable for group chats) */}
          <Typography variant="overline" color="text.secondary">Group Name</Typography>
          {isGroup && editingName ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={savingName}
                autoFocus
              />
              <IconButton
                size="small"
                color="primary"
                onClick={handleSaveName}
                disabled={savingName || !editName.trim()}
              >
                {savingName ? <CircularProgress size={18} /> : <CheckIcon />}
              </IconButton>
              <IconButton size="small" onClick={() => setEditingName(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 0.5 }}>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {displayName}
              </Typography>
              {isGroup && (
                <IconButton size="small" onClick={handleStartEditName} title="Rename group">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Members list */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
              Members ({participantList.length})
            </Typography>
            {isGroup && !addingMembers && (
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleStartAddMembers}
              >
                Add
              </Button>
            )}
          </Box>

          {/* Add members section */}
          {addingMembers && (
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableMembers}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedNewMembers}
                onChange={(_, newValue) => setSelectedNewMembers(newValue)}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                      <Avatar src={option.photoURL} sx={{ width: 28, height: 28 }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search members to add..."
                    sx={{ mb: 1 }}
                  />
                )}
                noOptionsText="No more members to add"
                disableCloseOnSelect
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveNewMembers}
                  disabled={selectedNewMembers.length === 0 || savingMembers}
                  startIcon={savingMembers ? <CircularProgress size={16} /> : <PersonAddIcon />}
                >
                  {savingMembers ? 'Adding...' : `Add ${selectedNewMembers.length || ''}`}
                </Button>
                <Button size="small" onClick={() => setAddingMembers(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          <List dense>
            {participantList.map((member) => (
              <ListItem key={member.id}>
                <ListItemAvatar>
                  <Avatar src={member.photoURL} sx={{ width: 32, height: 32 }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.isCurrentUser ? `${member.name} (You)` : member.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default ConversationView;
